import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { customStyles } from '../../helpers/StyleUtils';
import { registrationService } from '../../api/registrationService';
import viewIcon from '../../images/view.png';
import hideIcon from '../../images/hide.png';

const NewUserRegistration = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firebase_id: '',
    email: '',
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'user',
    is_active: true
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Role options. Keep in sync with the roles AppNavbar/ProtectedRoute actually handle —
  // a role missing here simply cannot be assigned when creating a user.
  const roleOptions = [
    { value: 'superadmin', label: 'Super Admin' },
    { value: 'planning', label: 'Planning' },
    { value: 'talent', label: 'Talent' },
    { value: 'finance', label: 'Finance' }
  ];

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate username from email if email field is changed
    if (name === 'email' && value) {
      const emailUsername = value.split('@')[0]; // Get part before @
      setFormData(prev => ({
        ...prev,
        email: value,
        username: emailUsername // Auto-set username to email prefix
      }));
    }
  };

  // Handle role selection
  const handleRoleChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      role: selectedOption.value
    }));
  };
  // Validate form
  const validateForm = () => {
    const errors = [];

    if (!formData.email) {
      errors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.push('Email must be valid');
    }

    // Username validation removed - will be auto-generated from email

    if (!formData.password) {
      errors.push('Password is required');
    } else if (formData.password.length < 6) {
      errors.push('Password must be at least 6 characters');
    }

    if (!formData.first_name) {
      errors.push('First name is required');
    }

    if (!formData.last_name) {
      errors.push('Last name is required');
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate form
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      setLoading(false);
      return;
    }

    try {
      const response = await registrationService.registerUser(formData);
      const result = response.data;

      if (result.success) {
        setSuccess(true);
        setFormData({
          firebase_id: '',
          email: '',
          username: '',
          password: '',
          first_name: '',
          last_name: '',
          role: 'user',
          is_active: true
        });
        
        // Show success message for 3 seconds then redirect
        setTimeout(() => {
          navigate('/users');
        }, 3000);
      } else {
        setError(result.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.message || error.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
      <div style={{ 
        backgroundColor: '#fff', 
        padding: '30px',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h1 style={{ 
            color: '#333',
            fontSize: '28px',
            fontWeight: '600',
            margin: 0
          }}>
            Register New User
          </h1>
        </div>

        {error && (
          <div style={{ 
            backgroundColor: '#fee', 
            color: '#c33', 
            padding: '12px', 
            borderRadius: '4px', 
            marginBottom: '20px',
            border: '1px solid #fcc'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ 
            backgroundColor: '#efe', 
            color: '#363', 
            padding: '12px', 
            borderRadius: '4px', 
            marginBottom: '20px',
            border: '1px solid #cfc'
          }}>
            ✅ User registered successfully! Redirecting to users page...
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off">
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="inputtext"
              placeholder="user@example.com"
              autoComplete="off"
              required
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Username * (Auto-generated from email)
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              className="inputtext"
              style={{ backgroundColor: '#e6e6e6'}}
              placeholder="Will be auto-generated from email"
              required
              readOnly
              disabled
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Auto-generated from email address.
            </small>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Password *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="inputtext"
                style={{ paddingRight: '40px' }}
                placeholder="Enter password"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#666'
                }}
              >
                <img 
                  src={showPassword ? hideIcon : viewIcon} 
                  alt={showPassword ? 'Hide password' : 'Show password'}
                  style={{ width: '20px', height: '20px' }}
                />
              </button>
            </div>
            <small style={{ color: '#666', fontSize: '12px' }}>
              Minimum 6 characters
            </small>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              First Name *
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              className="inputtext"
              placeholder="John"
              required
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Last Name *
            </label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              className="inputtext"
              placeholder="Doe"
              required
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Role
            </label>
            <Select
              value={roleOptions.find(option => option.value === formData.role)}
              onChange={handleRoleChange}
              options={roleOptions}
              styles={{
                ...customStyles,
                control: (base) => ({
                  ...customStyles.control(base),
                  width: '100%',
                  maxWidth: '100%',
                })
              }}
              placeholder="Select role"
            />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontWeight: '500' }}>Active User</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '15px' }}>
            <button
              type="submit"
              disabled={loading}
              className='big-button'
              style={{
                flex: 1,
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              {loading ? 'Creating User...' : 'Create User'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/users')}
              className='big-button'
              style={{
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
          </div>
        </form>

        <div style={{ 
          marginTop: '30px', 
          padding: '15px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '4px',
          fontSize: '14px',
          color: '#666'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Registration Info</h4>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li>User will receive a welcome email with login credentials</li>
           <li>Password will be securely hashed</li>
            <li>User will be active immediately</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NewUserRegistration; 